import React, {useEffect, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {
    HomeIcon, 
    ArrowRightOnRectangleIcon, 
    Bars3Icon, 
    UserGroupIcon,
    CalendarIcon,
    Cog6ToothIcon,
    XMarkIcon,
    AcademicCapIcon
} from '@heroicons/react/24/outline';
import TokenCount from "./ToeknCount";
import './NavMenu.css';
import Helper from "../helpers";

const NavMenu = () => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [userName, setUserName] = useState("");
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const navigate = useNavigate();

    useEffect(() => {
        const handleResize = () => {
            const newIsMobile = window.innerWidth < 768;
            const wasDrawerOpen = drawerOpen;
            
            setIsMobile(newIsMobile);
            
            // Auto-hide drawer when switching screen sizes
            if (wasDrawerOpen && (newIsMobile !== isMobile)) {
                setDrawerOpen(false);
            }
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [drawerOpen, isMobile]);

    useEffect(() => {
        const checkAdmin = async () => {
            const admin = await Helper.userIsAdmin();
            setIsAdmin(admin);
        };
        const checkLoginStatus = async () => {
            const token = await Helper.getCookie('wh-jwt');
            if (token) {
                setIsLoggedIn(true);
                const decoded = Helper.parseJwt(token);
                setUserName(decoded.given_name || decoded.user_name || "User");
            } else {
                setIsLoggedIn(false);
            }
        };
        checkAdmin();
        checkLoginStatus();
        const interval = setInterval(checkLoginStatus, 5000);
        return () => clearInterval(interval);
    }, []);

    const handleNav = (path) => {
        setDrawerOpen(false);
        navigate(path);
    };

    const NavLink = ({ to, icon: Icon, children, isAdmin: adminRequired = false, onClick }) => {
        if (adminRequired && !isAdmin) return null;
        
        const baseClasses = "flex items-center px-4 py-3 text-gray-300 hover:text-white hover:bg-gray-700 transition-colors duration-200 rounded-lg mx-2 mb-1";
        const adminClasses = adminRequired ? "border-l-2 border-gray-600" : "";
        
        if (onClick) {
            return (
                <button onClick={onClick} className={`${baseClasses} ${adminClasses} w-full text-left`}>
                    <Icon className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span className="font-medium">{children}</span>
                </button>
            );
        }
        
        return (
            <Link to={to} onClick={() => setDrawerOpen(false)} className={`${baseClasses} ${adminClasses}`}>
                <Icon className="h-5 w-5 mr-3 flex-shrink-0" />
                <span className="font-medium">{children}</span>
            </Link>
        );
    };

    return (
        <>
            {/* Fixed Navigation Bar */}
            <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-lg">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        {/* Logo */}
                        <Link to="/" className="flex pl-6 items-center space-x-2 text-white hover:opacity-80 transition-opacity">
                            <img
                                src="/img/wh-logo-20260425.png"
                                alt="WorldsHighStreet"
                                className="h-10 w-10 rounded-full object-cover flex-shrink-0"
                            />
                            <span className="text-xl font-bold hidden sm:block">WorldsHighStreet</span>
                        </Link>

                        {/* Desktop Navigation */}
                        {!isMobile && isLoggedIn && (
                            <div className="hidden md:flex items-center space-x-4">
                                <Link 
                                    to="/dashboard" 
                                    className="text-gray-300 hover:text-white px-3 py-2 rounded-md font-medium transition-colors"
                                >
                                    Home
                                </Link>
                                <Link 
                                    to="/dashboard/sessions" 
                                    className="text-gray-300 hover:text-white px-3 py-2 rounded-md font-medium transition-colors"
                                >
                                    Sessions
                                </Link>
                                {isAdmin && (
                                    <Link 
                                        to="/dashboard/adminSession" 
                                        className="text-gray-400 hover:text-gray-200 px-3 py-2 rounded-md font-medium transition-colors"
                                    >
                                        Admin
                                    </Link>
                                )}
                            </div>
                        )}

                        {/* Right side items */}
                        <div className="flex items-center space-x-4">
                            {/* Login/Logout for non-logged in users */}
                            {!isLoggedIn && (
                                <>
                                    <Link 
                                        to="/login" 
                                        className="text-gray-300 hover:text-white px-3 py-2 rounded-md font-medium transition-colors"
                                    >
                                        Login
                                    </Link>
                                    {/* Menu button for non-logged in users */}
                                    <button 
                                        onClick={() => setDrawerOpen(!drawerOpen)}
                                        className="p-2 rounded-lg bg-gray-800 text-white hover:bg-gray-700 transition-colors"
                                    >
                                        <Bars3Icon className="h-6 w-6" />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </nav>

            {/* Mobile/Drawer Menu - Only for non-logged in users */}
            {drawerOpen && !isLoggedIn && (
                <>
                    {/* Backdrop */}
                    <div 
                        className="fixed inset-0 bg-black/50 z-40"
                        onClick={() => setDrawerOpen(false)}
                    ></div>
                    
                    {/* Drawer */}
                    <div className="fixed top-0 right-0 h-full w-80 max-w-sm bg-gray-900 z-50 shadow-xl transform transition-transform duration-300">
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b border-gray-700">
                            <h2 className="text-xl font-semibold text-white">Menu</h2>
                            <button 
                                onClick={() => setDrawerOpen(false)}
                                className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>

                        {/* User Info */}
                        {isLoggedIn && (
                            <div className="p-4 border-b border-gray-700">
                                <div className="flex items-center space-x-3">
                                    <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center">
                                        <span className="text-white font-semibold text-lg">
                                            {userName.charAt(0).toUpperCase()}
                                        </span>
                                    </div>
                                    <div>
                                        <p className="text-white font-medium">{userName}</p>
                                        <div className="mt-1">
                                            <TokenCount isLoggedIn={isLoggedIn} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Navigation Links */}
                        <div className="flex-1 py-4">
                            <NavLink to="/dashboard" icon={HomeIcon}>
                                Home
                            </NavLink>
                            
                            {isLoggedIn && (
                                <>
                                    <NavLink to="/dashboard/sessions" icon={CalendarIcon}>
                                        Sessions
                                    </NavLink>
                                    
                                    <NavLink to="/dashboard/classes" icon={AcademicCapIcon}>
                                        Classes
                                    </NavLink>
                                    
                                    {isAdmin && (
                                        <>
                                            <div className="px-4 py-2 mt-4 mb-2">
                                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                                    Admin
                                                </p>
                                            </div>
                                            
                                            <NavLink to="/dashboard/adminSession" icon={CalendarIcon} isAdmin={true}>
                                                Admin Sessions
                                            </NavLink>
                                            
                                            <NavLink to="/dashboard/adminClasses" icon={AcademicCapIcon} isAdmin={true}>
                                                Admin Classes
                                            </NavLink>
                                            
                                            <NavLink to="/dashboard/adminVenueManagment" icon={UserGroupIcon} isAdmin={true}>
                                                Manage Users
                                            </NavLink>

                                            {/*<NavLink to="/dashboard/plans" icon={ClipboardDocumentListIcon} isAdmin={true}>*/}
                                            {/*    Manage Plans*/}
                                            {/*</NavLink>*/}
                                        </>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Bottom Actions */}
                        {isLoggedIn && (
                            <div className="p-4 border-t border-gray-700 space-y-2">
                                <NavLink to="/dashboard/profile" icon={Cog6ToothIcon}>
                                    Profile
                                </NavLink>
                                <NavLink onClick={() => handleNav('/logout')} icon={ArrowRightOnRectangleIcon}>
                                    Logout
                                </NavLink>
                            </div>
                        )}
                    </div>
                </>
            )}
        </>
    );
};

export default NavMenu;